import React from "react";
import { MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import _ from "lodash";
import {
    SCHEDULE_PRESETS,
    WEEK_DAY_OPTIONS,
    dateToMinutes,
    detectSchedulePreset,
    hasScheduleWindow,
    minutesToDate,
    minutesToTimeValue,
} from "../types/alertRule/alertRuleConstants";

// En qué horario puede notificar una automatización (reglas y «Conexión de equipos»), en hora de Chile. Vive en la
// automatización y no como una condición más, porque no mira ninguna medición. `value` trae schedule_start_minute,
// schedule_end_minute y schedule_days; `onChange` recibe solo lo que cambia.
const ScheduleEditor = ({ value, onChange }) => {
    const schedulePreset = detectSchedulePreset(value);
    const scheduleDays = value?.schedule_days || [];
    const scheduleCrossesMidnight = hasScheduleWindow(value)
        && Number(value.schedule_start_minute) > Number(value.schedule_end_minute);

    const applySchedulePreset = (presetKey) => {
        const preset = _.find(SCHEDULE_PRESETS, { key: presetKey });
        if (!preset) return;
        onChange({
            schedule_start_minute: preset.start,
            schedule_end_minute: preset.end,
            schedule_days: [...preset.days],
        });
    };

    const updateScheduleMinute = (field, date) => {
        const minutes = dateToMinutes(date);
        if (minutes === null) return;
        onChange({ [field]: minutes });
    };

    return (
        <Stack spacing={1.25}>
            <TextField
                select
                size="small"
                label="Cuándo notificar"
                value={schedulePreset}
                onChange={(event) => applySchedulePreset(event.target.value)}
                sx={{ maxWidth: { xs: "100%", sm: 320 } }}>
                {_.map(SCHEDULE_PRESETS, (preset) => (
                    <MenuItem key={`schedule-preset-${preset.key}`} value={preset.key}>
                        {preset.label}
                    </MenuItem>
                ))}
            </TextField>

            {schedulePreset === "custom" && (
                <Stack spacing={1}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "flex-start" }}>
                            <TimePicker
                                label="Desde"
                                ampm={false}
                                value={minutesToDate(value?.schedule_start_minute)}
                                onChange={(date) => updateScheduleMinute("schedule_start_minute", date)}
                                slotProps={{ textField: { size: "small", sx: { width: { xs: "100%", sm: 150 } } } }}
                            />
                            <TimePicker
                                label="Hasta"
                                ampm={false}
                                value={minutesToDate(value?.schedule_end_minute)}
                                onChange={(date) => updateScheduleMinute("schedule_end_minute", date)}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { width: { xs: "100%", sm: 150 } },
                                        error: value?.schedule_start_minute === value?.schedule_end_minute,
                                        helperText: value?.schedule_start_minute === value?.schedule_end_minute
                                            ? "Debe ser distinta a la hora de inicio"
                                            : undefined,
                                    },
                                }}
                            />
                        </Stack>
                    </LocalizationProvider>

                    <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            Días en que notifica
                        </Typography>
                        <ToggleButtonGroup
                            size="small"
                            value={scheduleDays}
                            onChange={(_event, nextDays) => onChange({ schedule_days: nextDays })}
                            sx={{
                                "& .MuiToggleButton-root": { px: 1.2, py: 0.5, textTransform: "none", fontWeight: 700 },
                                "& .MuiToggleButton-root.Mui-selected": {
                                    backgroundColor: "primary.main",
                                    color: "common.white",
                                    "&:hover": { backgroundColor: "primary.dark" },
                                },
                            }}>
                            {_.map(WEEK_DAY_OPTIONS, (day) => (
                                <ToggleButton key={`schedule-day-${day.value}`} value={day.value}>
                                    {day.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Stack>

                    <Typography variant="caption" color={scheduleDays.length ? "text.secondary" : "error.main"}>
                        {scheduleDays.length
                            ? scheduleCrossesMidnight
                                ? `Los días marcados (en morado) notifican de 00:00 a ${minutesToTimeValue(value.schedule_end_minute)} y de ${minutesToTimeValue(value.schedule_start_minute)} a 24:00, en hora de Chile.`
                                : `Los días marcados (en morado) notifican de ${minutesToTimeValue(value.schedule_start_minute)} a ${minutesToTimeValue(value.schedule_end_minute)}, en hora de Chile.`
                            : "Marca al menos un día para poder guardar."}
                    </Typography>
                </Stack>
            )}
        </Stack>
    );
};

export default ScheduleEditor;
